import { Box, Container, Flex, StackItem, Text, VStack } from '@chakra-ui/react';
import { Link as CustomLink } from 'components/base/Link';
import { ColorBlockHero } from 'components/modules/ColorBlockHero';
import { LinkType } from 'lib/enums/LinkType';
import { ColorBlockHero as ColorBlockHeroModel, ContentPage as ContentPageModel, OfficePageContent } from 'models';
import ErrorPage from 'pages/error';
import { createElement } from 'react';
import { withTranslations } from 'store/translations';
import { mapper } from 'utils/componentMapper';

import People from '../ContactUs/People';
import { HeadComponent } from '../Head';
import OfficeInfo, { OfficeInfoProps } from './OfficeInfo';
import { CountriesWithOfficesCollection } from './types';

export type LocationProps = {
  contentPageModel?: ContentPageModel;
  offices: OfficeInfoProps[];
  lastSegment: string;
  countriesWithOffices: CountriesWithOfficesCollection;
};

const Location = ({ contentPageModel, offices, lastSegment, countriesWithOffices }: LocationProps) => {
  const { common, multiple } = withTranslations();

  const colorBlockHeroModel = contentPageModel?.elements.hero.linkedItems[0] as ColorBlockHeroModel;
  const pageContent = (contentPageModel?.elements.content.linkedItems[0] as OfficePageContent)?.elements.pageContent
    .linkedItems;
  const contentModulesMap = pageContent?.map((el) => {
    return {
      type: el.system.type,
      category: contentPageModel?.elements.category?.value[0]?.codename,
    };
  });
  const shouldShowAutomaticOffices =
    contentModulesMap && !contentModulesMap.find((el) => el.type === 'editorial_spots' && el.category === 'office');

  if (!contentPageModel) {
    return <div>This page is unavailable</div>;
  }
  const localizedToStandardMap: Record<string, string> = {
    Danmark: 'Denmark',
    Sverige: 'Sweden',
    Norge: 'Norway',
    Suomi: 'Finland',
    'United States': 'United States of America',
    'united-states': 'United States of America',
    Schweiz: 'Switzerland',
    japanese: 'Japan',
    italia: 'Italy',
    francais: 'France',
    polska: 'Poland',
  };

  const matchedCountry = countriesWithOffices?.all.find(
    (country) => country.url.toLowerCase() === lastSegment.toLowerCase(),
  );

  let countryTitle = matchedCountry
    ? localizedToStandardMap[matchedCountry.title] || matchedCountry.title
    : localizedToStandardMap[lastSegment] || lastSegment;

  if (lastSegment == 'spain-and-portugal' || lastSegment === 'espana-y-portugal') {
    countryTitle = 'Spain';
  }
  return (
    <>
      <HeadComponent page={contentPageModel} />
      <ColorBlockHero
        backLink={{
          href: `/${multiple('contactUsUrl')}`,
          label: common('contactUs'),
        }}
        model={colorBlockHeroModel}
      />
      {shouldShowAutomaticOffices && (
        <Container py={{ base: 'm', md: 'l' }}>
          <Box mb={{ base: 's', md: 'l' }}>
            <Flex>
              <Text as='h2' color='mountain.base' flex={1} textStyle='desktop.h5'>
                {common('offices')}
              </Text>
              <Box display={{ base: 'none', sm: 'block' }}>
                <CustomLink
                  href={`/${multiple('contactUsUrl')}`}
                  iconName='chevronRightForLink'
                  type={LinkType.SecondaryButton}
                  // locale={router.locale}
                >
                  {common('viewAll')}
                </CustomLink>
              </Box>
            </Flex>
          </Box>
          <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 'm', md: 'l' }}>
            {offices.map(({ id, ...office }) => (
              <OfficeInfo key={id} flex={1} {...office} />
            ))}
          </Flex>
        </Container>
      )}
      {!!pageContent?.length && (
        <VStack shouldWrapChildren spacing={0}>
          {pageContent
            .filter((element: any) => !['download'].includes(element.system.type))
            .map((element: any, index: number) => (
              <StackItem key={index} display='block'>
                {createElement(mapper(element.system.type) as any, { model: element })}
              </StackItem>
            ))}
        </VStack>
      )}

      <div id='people-section'>
        <People noOfPeople={9} countryChosen={countryTitle} />
      </div>

      {!!pageContent?.length && (
        <VStack shouldWrapChildren spacing={0}>
          {pageContent
            .filter((element: any) => element.system.type === 'download')
            .map((element: any, index: number) => (
              <StackItem key={index} display='block'>
                {createElement(mapper(element.system.type) as any, { model: element })}
              </StackItem>
            ))}
        </VStack>
      )}
    </>
  );
};

export default Location;
